import { useRef, useState } from 'react';
import { Gift, Loader2 } from 'lucide-react';
import {
  GeneratedGiftcard,
  GIFTCARD_VALUES,
  GiftcardValue,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  PaymentMethod,
} from '../../../interfaces/giftcard.interface';
import {
  createManualGiftcard,
  giftcardErrorMessage,
} from '../../../services/giftcards.service';

// Le gabarit PNG n'affiche qu'une ligne par champ (même limite que le site public).
const TEXT_LIMIT = 40;
const NOTE_LIMIT = 200;

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#623413]/40';

const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/** Une clé par bon : rejouer le même envoi (double-clic, retry) ne crée pas de doublon. */
const newIdempotencyKey = (): string => {
  const cryptoObj = window.crypto as Crypto & { randomUUID?: () => string };
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

interface Props {
  onCreated: (giftcard: GeneratedGiftcard, emailRequested: boolean) => void;
}

const GiftcardForm = ({ onCreated }: Props) => {
  const [value, setValue] = useState<GiftcardValue>(60);
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [message, setMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idempotencyKey = useRef(newIdempotencyKey());

  const resetForm = () => {
    setTo('');
    setFrom('');
    setMessage('');
    setEmail('');
    setSendEmail(false);
    setNote('');
    setValue(60);
    setPaymentMethod('cash');
    idempotencyKey.current = newIdempotencyKey();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!to.trim()) return setError('Le nom du bénéficiaire est requis.');
    if (!from.trim()) return setError("Le nom de l'offrant est requis.");
    if (sendEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setError("L'e-mail est invalide (requis pour l'envoi).");
    }

    setLoading(true);
    try {
      const giftcard = await createManualGiftcard({
        value,
        to: to.trim(),
        from: from.trim(),
        message: message.trim() || undefined,
        email: email.trim() || undefined,
        paymentMethod,
        note: note.trim() || undefined,
        sendEmail,
        idempotencyKey: idempotencyKey.current,
      });

      onCreated(giftcard, sendEmail);
      resetForm();
    } catch (err) {
      console.error('❌ Erreur génération du bon :', err);
      setError(giftcardErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const counterClass = (length: number) =>
    `mt-1 text-xs ${length >= TEXT_LIMIT ? 'text-[#BB0F33] font-medium' : 'text-gray-400'}`;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-[#623413]/10 shadow-sm p-6 space-y-5"
    >
      <div>
        <span className={labelClass}>Montant du bon *</span>
        <div className="grid grid-cols-4 gap-2">
          {GIFTCARD_VALUES.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => setValue(amount)}
              className={`rounded-xl border py-3 font-semibold transition-all ${
                value === amount
                  ? 'border-[#623413] bg-[#623413] text-white'
                  : 'border-gray-300 text-gray-700 hover:border-[#623413]/50'
              }`}
            >
              {amount} €
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor="giftcard-to">
            Pour (bénéficiaire) *
          </label>
          <input
            id="giftcard-to"
            className={inputClass}
            placeholder="Ex : Marie Dupont"
            value={to}
            maxLength={TEXT_LIMIT}
            onChange={(e) => setTo(e.target.value)}
            autoFocus
          />
          <p className={counterClass(to.length)}>
            {to.length}/{TEXT_LIMIT}
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="giftcard-from">
            De la part de *
          </label>
          <input
            id="giftcard-from"
            className={inputClass}
            placeholder="Ex : Paul Martin"
            value={from}
            maxLength={TEXT_LIMIT}
            onChange={(e) => setFrom(e.target.value)}
          />
          <p className={counterClass(from.length)}>
            {from.length}/{TEXT_LIMIT}
          </p>
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="giftcard-message">
          Message sur le bon
        </label>
        <input
          id="giftcard-message"
          className={inputClass}
          placeholder="Ex : Joyeux anniversaire !"
          value={message}
          maxLength={TEXT_LIMIT}
          onChange={(e) => setMessage(e.target.value)}
        />
        <p className={counterClass(message.length)}>
          {message.length}/{TEXT_LIMIT} — une seule ligne s'affiche sur le bon
        </p>
      </div>

      <div>
        <label className={labelClass} htmlFor="giftcard-payment">
          Paiement encaissé *
        </label>
        <select
          id="giftcard-payment"
          className={inputClass}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
        >
          {PAYMENT_METHODS.map((method) => (
            <option key={method} value={method}>
              {PAYMENT_METHOD_LABELS[method]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-400">
          {paymentMethod === 'offert'
            ? 'Aucun montant encaissé ne sera enregistré.'
            : `${value} € seront enregistrés comme encaissés.`}
        </p>
      </div>

      <div>
        <label className={labelClass} htmlFor="giftcard-email">
          E-mail du client
        </label>
        <input
          id="giftcard-email"
          type="email"
          className={inputClass}
          placeholder="exemple@mail.com (facultatif)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label className="mt-2 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="w-4 h-4 accent-[#623413]"
          />
          <span className="text-sm text-gray-700">
            Envoyer aussi le bon par e-mail
          </span>
        </label>
      </div>

      <div>
        <label className={labelClass} htmlFor="giftcard-note">
          Note interne
        </label>
        <input
          id="giftcard-note"
          className={inputClass}
          placeholder="Ex : vendu en boutique le samedi"
          value={note}
          maxLength={NOTE_LIMIT}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-400">
          Visible uniquement dans le dashboard, jamais sur le bon.
        </p>
      </div>

      {error && (
        <p className="text-sm text-[#BB0F33] bg-[#BB0F33]/5 border border-[#BB0F33]/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#623413] text-white font-medium hover:opacity-90 disabled:opacity-60 transition"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Génération du bon...
          </>
        ) : (
          <>
            <Gift size={18} />
            Générer le bon
          </>
        )}
      </button>
    </form>
  );
};

export default GiftcardForm;
